import React, { useState, useEffect, useContext } from 'react';
import "./AddStaff.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import Input from '../../Components/Input/Input';
import DropDown from '../../Components/DropDown/DropDown';
import Message from '../../Components/Message/Message';

import GlobalContext from "../../context/GlobalContext";

import { LuPackagePlus } from "react-icons/lu";
import { FaRegSave } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import { TbFileDescription } from "react-icons/tb";
import { MdDriveFileRenameOutline, MdOutlinePriceCheck } from 'react-icons/md';
import { IoMdArrowDropdown } from "react-icons/io";
import { IoAddOutline, IoCubeOutline } from "react-icons/io5";
import { FaUserLock } from "react-icons/fa";
import { BsPeopleFill } from "react-icons/bs";

import { MdOutlineAttachEmail } from "react-icons/md";
import { MdOutlinePhone } from "react-icons/md";
import { FaKey } from "react-icons/fa6";
import { useTranslation } from 'react-i18next';


import { create_staff, get_staff } from "../../context/Services/Staff";
import { get_all_stock } from "../../context/Services/Stock";
import {
	get_commercial_products,
	bulk_assign_products,
	unassign_product
} from "../../context/Services/Settings";

const ROLES = [
	{ value: "admin", name: "Admin" },
	{ value: "employee", name: "Employé" },
	{ value: "commercial", name: "Commercial" },
]

const AddStaff = () => {
	const { stores } = useContext(GlobalContext);
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();

	const path = location.pathname;
	const params = new URLSearchParams(location.search);
	const query = Object.fromEntries(params.entries());

	let initial_payload = {
		first_name: "",
		last_name: "",
		email: "",
		telephone: "",
		note: "",
		role: "",
		password: "",
		store: null
	}

	const [staff, setStaff] = useState(initial_payload);
	const [loading, setLoading] = useState(false);
	const [readOnly, setReadOnly] = useState(false);

	// Product assignment states
	const [allProducts, setAllProducts] = useState([]);
	const [assignedProducts, setAssignedProducts] = useState([]);
	const [productSearchQuery, setProductSearchQuery] = useState("");
	const [assignmentLoading, setAssignmentLoading] = useState(false);

	const handle_create_staff = async () => {
		setLoading(true);

		try {
			await create_staff(staff);

			// If editing a commercial, also save product assignments
			if (id && staff.role === "commercial") {
				await bulk_assign_products(id, null, assignedProducts);
			}

			setTimeout(() => {
				setLoading(false);
				navigate("/staff");
			}, 500);
		} catch (e) {
			console.log(e);
			setLoading(false);
		}
	}

	// Fetch all products for assignment
	const handle_get_products = async () => {
		try {
			const res = await get_all_stock({ query: "" });
			const stockData = res?.data?.stock || res?.stock || [];
			setAllProducts(stockData);
		} catch (e) {
			console.log(e);
		}
	}

	// Fetch assigned products for this commercial
	const handle_get_assigned_products = async (commercialId) => {
		try {
			const res = await get_commercial_products(commercialId);
			const data = res?.data || [];
			// Extract assignment details
			const assignments = data.map(item => ({
				stock_id: item.product?._id || item.stock_id,
				amount: item.amount || 0,
				base_price: item.base_price || 0
			})).filter(a => a.stock_id);
			setAssignedProducts(assignments);
		} catch (e) {
			console.log(e);
		}
	}

	// Toggle product assignment
	const handle_toggle_product = (product) => {
		const productId = product._id;
		const isAssigned = assignedProducts.some(p => p.stock_id === productId);

		if (isAssigned) {
			setAssignedProducts(prev => prev.filter(p => p.stock_id !== productId));
		} else {
			setAssignedProducts(prev => [...prev, {
				stock_id: productId,
				amount: 0,
				base_price: product.prix_de_vente || product.sell_price || 0
			}]);
		}
	}

	// Handle assignment field change
	const handle_assignment_change = (productId, field, value) => {
		setAssignedProducts(prev => prev.map(p => 
			p.stock_id === productId ? { ...p, [field]: parseFloat(value) || 0 } : p
		));
	}

	// Save product assignments
	const handle_save_assignments = async () => {
		if (!id) return;

		setAssignmentLoading(true);
		try {
			await bulk_assign_products(id, null, assignedProducts);
			// Show success message
			setAssignmentLoading(false);
		} catch (e) {
			console.log(e);
			setAssignmentLoading(false);
		}
	}

	// Select/deselect all products
	const handle_select_all = () => {
		if (assignedProducts.length === allProducts.length) {
			setAssignedProducts([]);
		} else {
			setAssignedProducts(allProducts.map(p => ({
				stock_id: p._id,
				amount: 0,
				base_price: p.prix_de_vente || p.sell_price || 0
			})));
		}
	}

	// Filter products by search query
	const filteredProducts = allProducts.filter(
		p => p.product_name?.toLowerCase().includes(productSearchQuery.toLowerCase())
	);

	useEffect(() => {
		if (id) {
			get_staff(id)
				.then(res => {
					if (query?.action === "readonly") {
						setReadOnly(true);
					}

					const data = res.data;
					setStaff({
						...data,
						first_name: data?.full_name.split(" ")[0],
						last_name: data?.full_name.split(" ")[1]
					})

					// If commercial, load assigned products
					if (data?.role === "commercial") {
						handle_get_assigned_products(id);
					}
				})
				.catch(e => console.log(e))
		}
	}, [id])

	// Load products when role is commercial
	useEffect(() => {
		if (staff.role === "commercial") {
			handle_get_products();
		}
	}, [staff.role])

	return (
		<>
			<Message loading={loading || assignmentLoading} status={{ status: loading || assignmentLoading }} />

			<div className="clients_overview latest_clients_overview shadow h-full">
				<div className="chart_section_header flex items-center gap">
					<BsPeopleFill style={{ fontSize: "1.5rem" }} />
					<b>{t("Add New Staff")}</b>

					<div className='relative to-right flex gap'>

						<div className="flex gap items-center filter_button" onClick={() => navigate("/staff")}>
							<RxCross2 style={{ color: "red", fontSize: '1.2rem' }} />
							<b>{t("Cancel")}</b>
						</div>

						<div className="flex gap items-center filter_button" onClick={handle_create_staff}>
							<FaRegSave style={{ color: "#7983ff", fontSize: '1.2rem' }} />
							<b>{t("Save")}</b>
						</div>

					</div>
				</div>
				<div className="main_info flex gap">
					<div className="general_info">
						<b>{t("General information")}</b>

						<div className="form_container flex flex-col gap">
							<div className='w-full flex gap'>
								<div className="flex flex-col half_w" style={{ gap: "10px" }}>
									<h5>{t("Staff FirstName")}</h5>
									<Input
										icon={<MdDriveFileRenameOutline className="icon_m" />}
										placeholder={t("Staff FirstName")}
										setChange={(e) => setStaff(prev => ({ ...prev, first_name: e }))}
										value={staff.first_name}
										readOnly={readOnly}
									/>
								</div>
								<div className="flex flex-col half_w" style={{ gap: "10px" }}>
									<h5>{t("Staff LastName")}</h5>
									<Input
										icon={<MdDriveFileRenameOutline className="icon_m" />}
										placeholder={t("Staff LastName")}
										setChange={(e) => setStaff(prev => ({ ...prev, last_name: e }))}
										value={staff.last_name}
										readOnly={readOnly}
									/>
								</div>
							</div>
							<div className="flex flex-col" style={{ gap: "10px" }}>
								<h5>{t("Email")}</h5>
								<Input
									icon={<MdOutlineAttachEmail className="icon_m" />}
									placeholder={t("Email")}
									setChange={(e) => setStaff(prev => ({ ...prev, email: e }))}
									value={staff.email}
									readOnly={readOnly}
								/>
							</div>
							<div className="flex flex-col" style={{ gap: "10px" }}>
								<h5>{t("Telephone")}</h5>
								<Input
									icon={<MdOutlinePhone className="icon_m" />}
									placeholder={t("Telephone")}
									setChange={(e) => setStaff(prev => ({ ...prev, telephone: e }))}
									value={staff.telephone}
									readOnly={readOnly}
								/>
							</div>

							<div className="flex flex-col" style={{ gap: "10px" }}>
								<h5>{t("Note")}</h5>
								<Input
									icon={<TbFileDescription className="icon_m" />}
									placeholder={t("Note")}
									setChange={(e) => setStaff(prev => ({ ...prev, note: e }))}
									value={staff.note}
									readOnly={readOnly}
								/>
							</div>

							<div className="flex flex-col" style={{ gap: "10px" }}>
								<h5>{t("Password")}</h5>
								<Input
									icon={<FaKey className="icon_m" />}
									placeholder={t("Password")}
									setChange={(e) => setStaff(prev => ({ ...prev, password: e }))}
									value={staff.password}
									readOnly={readOnly}
								/>
							</div>

							<div className="flex flex-col" style={{ gap: "10px" }}>
								<h5>{t("Role")}</h5>
								<div className='relative full'>
									<DropDown
										fullWidth={true}
										customButtonStyle="justify-start category_dropdown"
										dropdownStyle="dropup"
										readOnly={readOnly}
										button={
											<>
												<FaUserLock />
												<b>
													{staff?.role !== "" ? ROLES.find(elem => elem?.value === staff?.role)?.name : t("All Roles")}
												</b>
												<IoMdArrowDropdown className="to-right" />
											</>
										}
									>
										{
											ROLES?.map((elem, i) => (
												<div className='drop_item' key={i} onClick={() => setStaff(prev => ({ ...prev, role: elem?.value }))}>
													{elem?.name}
												</div>
											))
										}
									</DropDown>
								</div>
							</div>
						</div>
					</div>

					{/* Product Assignment Section - Only for commercials */}
					{staff.role === "commercial" && id && (
						<div className="product_assignment_section">
							<div className="flex items-center gap" style={{ marginBottom: "15px" }}>
								<IoCubeOutline style={{ fontSize: "1.3rem", color: "#7983ff" }} />
								<b>{t("Produits Assignés")}</b>
							</div>

							<p style={{ fontSize: "12px", color: "#666", marginBottom: "15px" }}>
								{t("Sélectionnez les produits que ce commercial peut vendre")}
							</p>

							<div className="product_assignment_controls flex gap" style={{ marginBottom: "15px" }}>
								<Input
									placeholder={t("Rechercher un produit...")}
									setChange={(e) => setProductSearchQuery(e)}
									value={productSearchQuery}
									customInputStyle="search_input_edited"
									customParentStyle="width_90"
								/>
								<button
									className="filter_button"
									onClick={handle_select_all}
								>
									{assignedProducts.length === allProducts.length
										? t("Désélectionner tout")
										: t("Sélectionner tout")}
								</button>
							</div>

							<div className="product_assignment_stats" style={{
								display: "flex",
								gap: "10px",
								marginBottom: "15px",
								fontSize: "12px"
							}}>
								<span style={{
									backgroundColor: "#e8f5e9",
									padding: "5px 10px",
									borderRadius: "4px",
									color: "#2e7d32"
								}}>
									{assignedProducts.length} {t("sélectionnés")}
								</span>
								<span style={{
									backgroundColor: "#f5f5f5",
									padding: "5px 10px",
									borderRadius: "4px",
									color: "#666"
								}}>
									{allProducts.length} {t("total")}
								</span>
							</div>

							<div className="product_assignment_list" style={{
								maxHeight: "500px",
								overflowY: "auto",
								padding: "10px"
							}}>
								{filteredProducts.length === 0 ? (
									<div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
										{t("Aucun produit trouvé")}
									</div>
								) : (
									<div className="product_assignment_grid_new">
										{filteredProducts.map(product => {
											const assignment = assignedProducts.find(p => p.stock_id === product._id);
											const isAssigned = !!assignment;

											return (
												<div
													key={product._id}
													className={`product_assignment_card ${isAssigned ? 'assigned' : ''}`}
												>
													<div className="flex_row items_center justify_between" style={{ marginBottom: isAssigned ? "12px" : "0" }}>
														<div className="flex_row items_center gap_10">
															<input
																type="checkbox"
																checked={isAssigned}
																onChange={() => !readOnly && handle_toggle_product(product)}
																disabled={readOnly}
																style={{ cursor: readOnly ? "default" : "pointer" }}
															/>
															<div className="flex_column">
																<b style={{ fontSize: "14px" }}>{product.product_name}</b>
																<span style={{ fontSize: "11px", color: "#666" }}>
																	{t("Stock global")}: {product.quantity}
																</span>
															</div>
														</div>
														<div className="flex_column items_end">
															<span style={{ fontSize: "14px", fontWeight: "600", color: "#7983ff" }}>
																{product.prix_de_vente || product.sell_price || 0} MAD
															</span>
														</div>
													</div>

													{isAssigned && (
														<div className="assignment_details_form flex_row gap_10" style={{ marginTop: "10px", borderTop: "1px dashed #e0e0e0", paddingTop: "10px" }}>
															<div className="flex_column flex_1" style={{ gap: "5px" }}>
																<label style={{ fontSize: "11px", color: "#666", fontWeight: "600" }}>{t("Quota (Quantité)")}</label>
																<input
																	type="number"
																	placeholder={t("Illimité if 0")}
																	value={assignment.amount}
																	onChange={(e) => handle_assignment_change(product._id, 'amount', e.target.value)}
																	disabled={readOnly}
																	style={{
																		padding: "8px 10px",
                                    height: "35px",
																		borderRadius: "6px",
																		border: "1px solid #e0e0e0",
																		fontSize: "13px"
																	}}
																/>
															</div>
															<div className="flex_column flex_1" style={{ gap: "5px" }}>
																<label style={{ fontSize: "11px", color: "#666", fontWeight: "600" }}>{t("Prix de base")}</label>
																<input
																	type="number"
																	placeholder={t("Prix min")}
																	value={assignment.base_price}
																	onChange={(e) => handle_assignment_change(product._id, 'base_price', e.target.value)}
																	disabled={readOnly}
																	style={{
																		padding: "8px 10px",
                                    height: "35px",
																		borderRadius: "6px",
																		border: "1px solid #e0e0e0",
																		fontSize: "13px"
																	}}
																/>
															</div>
														</div>
													)}
												</div>
											);
										})}
									</div>
								)}
							</div>
						</div>
					)}

					{staff.role === "commercial" && !id && (
						<div className="product_assignment_section">
							<div className="flex items-center gap" style={{ marginBottom: "15px" }}>
								<IoCubeOutline style={{ fontSize: "1.3rem", color: "#7983ff" }} />
								<b>{t("Produits Assignés")}</b>
							</div>
							<p style={{ padding: "30px", textAlign: "center", color: "#999", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
								{t("Enregistrez d'abord le commercial pour pouvoir assigner des produits")}
							</p>
						</div>
					)}


				</div>
			</div>
		</>
	)
}

export default AddStaff